import { DateTime } from 'luxon';
import { Log_Workout } from './log-workout';
import { workoutHoldsConcludedSet } from './workout-conclusion';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordState, LogRecordType, Log_Entry } from './user-log';

/**
 * Does this day entry hold anything the user put there, so that regenerating
 * the day must never delete it?
 *
 * Regeneration marks every entry in the rebuilt range as deleted and revives
 * only what it returns, so this is the question that decides whether a day's
 * entry survives a schedule change. Every place that needs the answer asks it
 * here, so none of them can answer it differently: the workout handler, for a
 * day that no longer schedules a workout; the floor that catches whatever the
 * handlers did not return; and that floor's slot-replacement branch, which asks
 * it of the entry ALREADY on the slot before discarding it.
 *
 * Two more askers are outside `schedule.ts`, where this used to live, and are
 * why it lives in a module of its own now. The delete-boundary backstop in
 * `user-db.delete-boundary.ts` runs after regeneration has returned and is the
 * last thing between this bug family and a real deletion; it asked the state
 * field directly until #4840's follow-up, so it could not see the shape
 * described below at all. And `pickOneEntryPerSingletonSlot` below asks it to
 * decide which document a screen renders, which it could not do while the rule
 * sat in `schedule.ts` — see the note on that function. A fourth COPY of the
 * rule is what this helper exists to prevent; a fourth CALLER is what it is for.
 *
 * Ordinarily the state field settles it: an entry still saying `Scheduled` is an
 * empty placeholder nobody has touched. #4840 is why that is not the whole
 * answer for a workout. A workout can be stored saying `Scheduled` while holding
 * sets the user finished — undoing a skip used to write exactly that, and so can
 * a copy of the document arriving from storage. Reading only the state field
 * deleted those sessions, silently and for good, and the alarm meant to catch it
 * stayed quiet because it asked the same corrupted field.
 *
 * Only workouts get that second question.
 *
 * The wake-up and bedtime entries (day start, day end) are the other way round
 * (#6431): their `Done` is written by the app, which ticks them as soon as the
 * day list shows them after their time. The user never does, so `Done` says
 * nothing about the user. Two devices in different time zones each build their
 * own copy of a day's wake-up entry, under different ids, and each ticks its own;
 * reading `Done` as user data then kept both forever. `Skipped` is still the
 * user's own choice, so it still counts. Meals, weigh-ins and weekly check-ups
 * are judged by state alone, as before.
 */
export function logEntryHoldsUserData(entry: Log_Entry): boolean {
  if (isDayBoundaryEntry(entry)) return entry.state !== LogRecordState.Scheduled && entry.state !== LogRecordState.Done;
  if (entry.state !== LogRecordState.Scheduled) return true;
  return entry.type === LogRecordType.Workout && workoutHoldsConcludedSet(entry as Log_Workout);
}

/**
 * How strongly one copy of a day's entry speaks for the user, when two copies of it disagree:
 * 2 when it holds user data, 1 when the app has moved it past `Scheduled` but it holds none,
 * 0 for an untouched placeholder. Two copies both holding data rank the same whatever their state
 * (#4840).
 *
 * The rule {@link pickOneEntryPerSingletonSlot} uses to choose which copy a screen shows, and the
 * one the conflict resolver uses to settle a merge's ties (#6825), so the two cannot disagree.
 * `holdsUserData` is passed in because the calendar's callers hold only part of each document and
 * work it out themselves; a caller holding a whole document passes `logEntryHoldsUserData(entry)`.
 */
export function logEntryCopyRank(entry: Partial<Pick<Log_Entry, 'state'>>, holdsUserData: boolean): 0 | 1 | 2 {
  if (holdsUserData) return 2;
  return entry.state !== LogRecordState.Scheduled ? 1 : 0;
}

/** Is this a wake-up (day start) or bedtime (day end) entry, whose `Done` the app writes itself? */
function isDayBoundaryEntry(entry: Pick<Log_Entry, 'type'>): boolean {
  return entry.type === LogRecordType.DayStart || entry.type === LogRecordType.DayEnd;
}

/**
 * The fields of an entry that {@link pickOneEntryPerSingletonSlot} reads, besides "holds user data".
 * `state` is optional: a caller without it simply does not get the second preference.
 */
export type SingletonSlotCandidate = Pick<Log_Entry, '_id' | 'type' | 'time_utc' | 'timeOffset_min'> & Partial<Pick<Log_Entry, 'state'>>;

/**
 * Reduce a day's one-per-day events to at most one entry each, and say which ones were left out.
 *
 * Every type except Meal happens exactly once a day — the wake-up, the sleep, the weigh-in, the
 * workout, the weekly check-up — so an event's slot is simply its type. A day can still end up
 * holding two documents for one of them: a log entry holding something the user did is never
 * deleted, so a reschedule or a second device can leave a completed entry beside an empty
 * placeholder. Every place that shows or reads one of these must apply this one rule, or they
 * pick different documents and show the user different answers.
 *
 * Which one wins: an entry holding user data beats one that does not; among entries holding none,
 * one the app has moved past `Scheduled` beats an untouched placeholder; and otherwise the later
 * one wins. The middle step exists for the wake-up and bedtime entries (#6431): a `Done` one holds
 * no user data, so a second copy of it may be deleted, but it is still the record of the target a
 * past day was eaten against, so it must win over an untouched placeholder. For every other type
 * the first two steps give the same answer.
 *
 * "Holds user data" is `logEntryHoldsUserData` above and nothing else — see the note
 * inside for why this function lives beside it rather than in `user-log.ts` with the entry types.
 * A caller holding only part of each document passes that function's answer in, never its own.
 * The ordering is done HERE rather than taken from the caller, since callers arrive in different
 * orders; ties fall back to `_id` so the answer depends only on the entries themselves.
 *
 * `referenceTime` only supplies a time zone to read the entries' wall-clock times in; any time on
 * the day being shown will do.
 *
 * The calendar's day marker holds small status rows rather than whole documents, so it passes
 * only the fields this rule reads, plus its own `holdsUserData` answer worked out from the whole
 * document when the row was built (#6281's follow-up). Every other caller passes documents and
 * leaves `holdsUserData` at its default.
 *
 * This is the one-per-day half of the rule `pickOneMealPerSlot` applies to meals, and it exists
 * for the same reason. #4511 is what having two rules cost: the Nutrition page took a day's
 * wake-up entry as whichever came first by time, while the day list preferred the one holding
 * data, so on a day holding two the two screens showed different calorie targets. See
 * `design-docs/sync-integrity.md`, "One Rule for Which Day an Entry Belongs To, and One Meal per
 * Slot".
 */
export function pickOneEntryPerSingletonSlot<T extends Log_Entry>(entries: T[], referenceTime: DateTime): { kept: T[]; dropped: T[] };
export function pickOneEntryPerSingletonSlot<T extends SingletonSlotCandidate>(
  entries: T[],
  referenceTime: DateTime,
  holdsUserData: (entry: T) => boolean,
): { kept: T[]; dropped: T[] };
export function pickOneEntryPerSingletonSlot<T extends SingletonSlotCandidate>(
  entries: T[],
  referenceTime: DateTime,
  // only a whole document can be asked the default question, which the first overload ensures
  holdsUserData: (entry: T) => boolean = entry => logEntryHoldsUserData(entry as unknown as Log_Entry),
): { kept: T[]; dropped: T[] } {
  const rank = (entry: T) => logEntryCopyRank(entry, holdsUserData(entry));
  const inSlotOrder = [...entries].sort((a, b) => {
    const aTime = getEntryLocalTimeInDesiredTimeZone(a, referenceTime).toMillis();
    const bTime = getEntryLocalTimeInDesiredTimeZone(b, referenceTime).toMillis();
    if (aTime !== bTime) return aTime - bTime;
    return (a._id || '') < (b._id || '') ? -1 : 1;
  });

  const winnerBySlot = new Map<string, T>();
  for (const entry of inSlotOrder) {
    // a meal is not a one-per-day event — it has its own rule, and passing one here is a
    // caller mistake rather than something to guess at, so leave it out of every slot
    if (entry.type === LogRecordType.Meal) continue;
    const key = String(entry.type);
    const previous = winnerBySlot.get(key);
    // take it if the slot is empty, or if the current winner ranks no higher (equal rank, and
    // this one is later in the sorted order)
    if (!previous || rank(entry) >= rank(previous)) {
      winnerBySlot.set(key, entry);
    }
  }

  const kept: T[] = [];
  const dropped: T[] = [];
  for (const entry of entries) {
    if (entry.type === LogRecordType.Meal) continue;
    (winnerBySlot.get(String(entry.type)) === entry ? kept : dropped).push(entry);
  }
  return { kept, dropped };
}
